import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import  QRCode  from 'react-qr-code';
import XCircleFillIcon from '../../../assets/icons/XCircleFill';
import Loader from '../../../components/Loader';
import { useSocket } from '../../../contexts/SocketContext';
import Toast from '../../../components/ToastNotification';

const ConnectEmployee = ({ selectedRow, onClose }) => {
    const { id: employee_id } = selectedRow;
    const [qrImage, setQrImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [countDownTimer, setCountDownTimer] = useState(null);
    const [importStatus, setImportStatus] = useState(null);
    const [linkCommand, setLinkCommand] = useState(null);
    const adminEmail = useSelector((state) => state.auth?.user?.email);
    const socket = useSocket();
    const qrTriggerCount = useRef(0);

    const startTimer = (duration) => {
        let timer = duration;
        const interval = setInterval(() => {
            timer = timer - 1;
            setCountDownTimer(timer);
        
            if (timer === 0) {
            clearInterval(interval);
            }
        }, 1000);
        }

    useEffect(() => {
        if (!socket) return;
        
        const handleQr = ({ employeeId, qr, attempt }) => {
            if (employee_id !== employeeId) return;

            qrTriggerCount.current = attempt;
            const duration = attempt === 1 ? 60 : 20
            startTimer(duration);
            setQrImage(qr);
            setLoading(false);
        };
    
        const failedConnection = ({ message }) => {
            Toast.error(message);
            setLoading(false);
            setQrImage(null);
            setCountDownTimer(null);
            setImportStatus(null);
            handleClose();
        }

        // Browser-import path (passkey): progress emitted by the server-side Puppeteer flow.
        const handleImportStatus = ({ employeeId, status, message }) => {
            if (employeeId && employee_id !== employeeId) return;
            setImportStatus({ status, message });
            if (status === 'failed') {
                Toast.error(message || 'Browser link failed');
                setLoading(false);
                setIsConnecting(false);
            }
        };

        socket.on('qr-generated', handleQr);
        socket.on('qr-expired', failedConnection);
        socket.on('reconnect-failed', failedConnection)
        socket.on('browser-import-status', handleImportStatus);

        return () => {
            socket.off('qr-generated', handleQr);
            socket.off('qr-expired');
            socket.off('reconnect-failed');
            socket.off('browser-import-status', handleImportStatus);
        };
    }, [socket, countDownTimer]);

    const handleConnect = async(e) => {
        e.preventDefault();
        setQrImage(null);
        try {
            if (!employee_id) {
                Toast.error('Please select employee')
                return;
            }

            if (!socket.connected) {
                Toast.error('Socket server not connected.');
                return;
            }

            setLoading(true);
            setIsConnecting(true);
            setImportStatus(null);
            socket.emit('join-employee-room',  { employeeId: employee_id});
            socket.emit('connect-whatsapp',  { employeeId: employee_id});

        } catch (error) {
            setLoading(false);
            setQrImage(null);
            Toast.error(error.message || error?.data?.message || 'An error occurred while connecting');
        }
    };

    // Passkey-blocked accounts: the browser + passkey must run on a machine with a screen and the phone
    // (never the headless server). Show the operator the command to run locally; it extracts + uploads
    // the session here, and the parent's 'connected' handler updates the UI once it links.
    const handleBrowserConnect = (e) => {
        e.preventDefault();
        if (!employee_id) {
            Toast.error('Please select employee');
            return;
        }

        const server = (process.env.REACT_APP_BASE_URL || window.location.origin).replace(/\/+$/, '');
        const email = adminEmail || '<your-portal-email>';
        setLinkCommand(`node src/scripts/linkEmployee.mjs --server ${server} --employee ${employee_id} --email ${email}`);

        if (socket?.connected) socket.emit('join-employee-room', { employeeId: employee_id });
    };

    const copyLinkCommand = () => {
        if (!linkCommand) return;
        navigator.clipboard?.writeText(linkCommand);
        Toast.success('Command copied');
    };

    const handleClose = () => {
        onClose();
        if (isConnecting) {
            socket.emit('cancel-session', { employeeId: employee_id})
        }
    }

return (
    <div className='overlay'>
        <div className='content_layout qr-container'>
            <p className='emp_info'>Employee:- {selectedRow?.name} ({selectedRow?.country_code  } {selectedRow?.phone_number})</p>
            <div className='qr_section'>
                <div className='content_header'>
                    <h2 className='content_header_heading'>Connect WhatsApp</h2>
                    <button className='close-btn' onClick={handleClose}><XCircleFillIcon size={24} /></button>
                </div>
                <div className='qr_body_section'>
                    <div className="qr_body">
                        {loading && <Loader/>}
                        {qrImage && <QRCode value={qrImage} className='qr_image'/>}
                        {!loading && !qrImage && <img className='reload' src="/rotate-right.png" onClick={handleConnect} alt="404" />}
                    </div>
                    {countDownTimer !== null && <p className='qr-countdown'>Expires in: {countDownTimer.toString().padStart(2,'0')}s</p>}
                    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                        <button
                            type='button'
                            onClick={handleBrowserConnect}
                            disabled={loading}
                            title='Use this if the QR scan gets stuck on the passkey step'
                            style={{
                                padding: '10px 16px',
                                border: 'none',
                                borderRadius: 8,
                                backgroundColor: loading ? '#b7b7b7' : '#5a67d8',
                                color: '#fff',
                                fontWeight: 600,
                                cursor: loading ? 'not-allowed' : 'pointer'
                            }}
                        >
                            Link via browser (passkey)
                        </button>
                        {linkCommand && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                                <p style={{ fontSize: 13, color: '#444', textAlign: 'center', margin: 0 }}>
                                    Run this on a computer with a screen and your phone nearby. Complete the WhatsApp
                                    login + passkey in the browser it opens — this updates when it connects.
                                </p>
                                <code style={{ display: 'block', background: '#1e1e1e', color: '#e6e6e6', padding: '10px 12px', borderRadius: 6, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                    {linkCommand}
                                </code>
                                <button type='button' onClick={copyLinkCommand} style={{ padding: '8px 12px', border: 'none', borderRadius: 8, backgroundColor: '#5a67d8', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                                    Copy command
                                </button>
                            </div>
                        )}
                        {importStatus && (
                            <p style={{ fontSize: 13, color: '#444', textAlign: 'center', margin: 0 }}>
                                {importStatus.message || importStatus.status}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </div>
)
}

export default ConnectEmployee;
